import { useEffect } from "react";

const DEFAULT_TITLE = "Podium — Hockey League Management";
const DEFAULT_DESCRIPTION =
  "Standings, schedules, stats and live scoring for amateur hockey leagues.";

/**
 * Finds a <meta> tag by attribute, creating it if the document doesn't have one.
 * index.html ships every tag we set here, so this only creates one if that file
 * and this hook ever drift apart.
 */
function metaTag(
  attribute: "name" | "property",
  value: string,
): HTMLMetaElement {
  const selector = `meta[${attribute}="${value}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, value);
    document.head.appendChild(tag);
  }

  return tag;
}

/**
 * Keeps the document title and description in sync with the page being viewed.
 *
 * NOTE: this is for people — browser tabs, bookmarks, history and the handful
 * of crawlers that execute JavaScript. Social crawlers (Slack, iMessage,
 * Discord, Facebook, X) never run this code, so link previews are generated
 * server-side instead; see docs/link-previews.md.
 *
 * @param title - Page title. Falls back to the Podium default when empty.
 * @param description - Page description. Falls back to the Podium default.
 */
export function usePageMetadata(
  title: string | null | undefined,
  description?: string | null,
) {
  useEffect(() => {
    const resolvedTitle = title || DEFAULT_TITLE;
    const resolvedDescription = description || DEFAULT_DESCRIPTION;

    document.title = resolvedTitle;
    metaTag("name", "description").content = resolvedDescription;
    metaTag("property", "og:title").content = resolvedTitle;
    metaTag("property", "og:description").content = resolvedDescription;
    metaTag("property", "og:url").content = window.location.href;
  }, [title, description]);
}
